import { useMutation, useQueryClient } from 'react-query';
import { useApiPost } from 'src/api';
import { useBuildVars } from 'src/config';
import { AcopDetails } from './acopList';
import { useSearchParams } from 'react-router-dom';
import { useAccount } from '@azure/msal-react';

export interface Product {
  acopTrackingId: number;
  acopcreated: string;
  customerCompanyCode: string;
  customerId: number;
  customerNumber: string;
  customersetId: 0;
  dealId: number;
  dealNumber: string;
  discountEuId: '';
  discountStatus: string;
  discountTrackingId: number;
  importreferenceId: number;
  ingramPartNumber: string;
  orderCreatedBy: string;
  orderDate: string;
  orderLineId: number;
  orderLineNbr: string;
  orderLineQuantity: string;
  orderNbr: string;
  orderQuantity: number;
  orderShipQuantity: string;
  uniqueDiscountTrackingId: number;
  vendorUniqueIdentifier: null;
}

export interface DeleteAcopRequest {
  requestBody: {
    bundleId: string;
    dmR_Action: string;
    eventType: string;
    products: Product[];
    reason: string;
  };
  requestPreamble: {
    clientSystem: string;
    companyCode: string;
    countryCode: string;
    user: string;
  };
}

export interface DeleteAcopResponse {
  dmR_Id: string;
  message: string;
  transactionID: string;
  transactionStatus: string;
}

export function useDeleteAcop(params: AcopDetails[], bundleId?: string) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const user = useAccount()?.username ?? '';
  const [searchParams] = useSearchParams();
  const companyCode = searchParams.get('companyCode') ?? '';
  const countryCode = searchParams.get('countryCode') ?? '';
  const apiPost = useApiPost<DeleteAcopResponse>({
    host: buildVars.VP_API_BASE,
  });
  const partialPayload = {
    requestBody: {
      ...(Boolean(bundleId) && { bundleId }),
      eventType: bundleId
        ? 'AUTO_ACOP_MAINTENANCE_BUNDLE'
        : 'AUTO_ACOP_MAINTENANCE',
      products: params.map((item) => ({
        acopTrackingId: item.acopTrackingNumber,
        acopcreated: item.createTimestamp,
        branchNo: item.branchNo,
        customerCompanyCode: item.customerCompanyCode,
        customerId: item.customerId,
        customerNumber: item.bcn,
        customersetId: 0,
        dealId: item.skSbo,
        dealNumber: item.bidNumber,
        discountEuId: '',
        discountStatus: item.statusCode,
        discountTrackingId: item.discountTrackingId,
        distNo: item.distNo,
        importreferenceId: item.importReferenceId,
        ingramPartNumber: item.sku,
        orderCreatedBy: item.createdBy,
        orderDate: item.orderDate,
        orderLineId: item.orderLineId,
        orderLineNbr: item.lineNumber,
        orderLineQuantity: item.orderLineQuantity,
        orderNbr: item.orderNumber,
        orderNo: item.orderNo,
        orderQuantity: item.quantity,
        orderShipQuantity: item.orderShipQuantity,
        shipNo: item.shipNo,
        uniqueDiscountTrackingId: item.uniqueDiscountTrackingId,
        vendorUniqueIdentifier: null,
      })),
    },
    requestPreamble: {
      clientSystem: 'APPGDEUI',
      companyCode,
      countryCode,
      user,
    },
  };

  return useMutation(
    (body: { dmR_Action: string; reason: string }) =>
      apiPost(
        '/system.webapi.gds/api/v1/gdsCoreApiDiscountMaintenance/DiscountMaintenanceServiceRequest',
        {
          ...partialPayload,
          requestBody: { ...partialPayload.requestBody, ...body },
        },
        undefined,
        {
          APPGDEUI: process.env.REACT_APP_APPGDEUI,
        }
      ),
    {
      onSuccess: () => {
        [
          '/system.webapi.gds/api/v1/gdsCoreApiDiscountMaintenance/GetDiscountDetails',
          '/system.webapi.gds/api/v1/gdsCoreApiDiscountMaintenance/GetDiscountStatusFilterCount',
        ].forEach((query) => queryClient.invalidateQueries(query));
      },
    }
  );
}
